import React from 'react';

import { ContractKeyGroup } from '@cube3/common/model/schema/resources/contract-key-group';
import { ContractValue } from '@cube3/common/model/schema/resources/contract-value';
import { MetadataCategory } from '@cube3/common/model/schema/resources/metadata-category';
import { MetadataValue } from '@cube3/common/model/schema/resources/metadata-value';
import {
  getFields,
  getFieldString
} from '@cube3/forms/src/utils/makeMetadataFields';
import { hydrateValue } from '@cube3/forms/src/utils/hydrateValue';

const getDefaultValueFromSchema = (schema: string) => {
  return schema && JSON.parse(schema)?.default;
};

// build initialValues object
// dehydrate some values (comma separated -> array)
export const useFormatMetadataValues = (
  values: Array<MetadataValue | ContractValue>,
  category: MetadataCategory | ContractKeyGroup,
  useDefault = false
) => {

  return React.useMemo(() => {
    const fields = getFields(category);
    const fieldString = getFieldString(category);
    const isMetaData = category?.type === 'metadata-category';

    const fieldIds: string[] = fields?.map((field) => field.id) || [];

    const retrievedValues: { [field_id: string]: unknown } =
      fields &&
      values &&
      values
        .filter((value) => fieldIds.indexOf(value[`${fieldString}_id`]) > -1)
        .reduce(
          // eslint-disable-next-line no-sequences
          (obj, value) => {
            const fieldType = fields.filter(
              (f) => f.id === value[`${fieldString}_id`]
            )[0].value_type;
            obj[`${fieldString}_${value[`${fieldString}_id`]}`] = hydrateValue(
              value.value,
              fieldType,
              true
            );
            return obj;
          },
          {}
        );

    const emptyFields = fieldIds.reduce((acc, val) => {
      acc[`${fieldString}_${val}`] = undefined;
      return acc;
    }, {});

    if (!useDefault) {
      return {
        ...emptyFields,
        ...retrievedValues
      };
    }

    const fieldsWithDefaultValues = fields?.filter(
      (f) => !!getDefaultValueFromSchema(f.json_schema)
    );
    const defaultValuesFromSchema = fieldsWithDefaultValues?.reduce(
      (obj: { [field_id: string]: any }, field) => {
        const fieldType = field.value_type;
        obj[`${fieldString}_${field.id}`] = hydrateValue(
          getDefaultValueFromSchema(field.json_schema),
          fieldType,
          true
        );
        return obj;
      },
      {}
    );

    // merge retrieved values and default values from schema
    const merged: { [field_id: string]: any } = retrievedValues || {};
    if (Object.keys(defaultValuesFromSchema)?.length) {
      Object.keys(defaultValuesFromSchema).forEach((field_id) => {
        if (!merged.hasOwnProperty(field_id)) {
          merged[field_id] = defaultValuesFromSchema[field_id];
        }
      });
    }

    return { ...emptyFields, ...merged };
  }, [values, category, useDefault]);
};
